import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, X, Check, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffirmationsModal = ({ goal, isPremium, onClose, onSelect }) => {
  const [affirmations, setAffirmations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [todaysAffirmation, setTodaysAffirmation] = useState(null);
  const [selectingIndex, setSelectingIndex] = useState(null);

  // Fetch today's selected affirmation on mount
  useEffect(() => {
    const fetchTodaysAffirmation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/affirmations/today`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.affirmation) {
            setTodaysAffirmation(data.affirmation.content);
          }
        }
      } catch (error) {
      }
    };
    
    if (isPremium) {
      fetchTodaysAffirmation();
    }
  }, [isPremium]);

  const generateAffirmations = async () => {
    if (!isPremium) {
      toast.error('AI affirmations are a Premium feature');
      return;
    }

    setLoading(true);
    setSelectedIndex(null);
    try {
      const response = await fetch(`${API_URL}/api/affirmations/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          identity_statement: goal.identity_statement,
          goal_title: goal.title,
          goal_description: goal.description,
          why: goal.why
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate affirmations');
      }

      const data = await response.json();
      setAffirmations(data.affirmations);
      setHasGenerated(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForVisualization = async (index) => {
    const affirmation = affirmations[index];
    setSelectingIndex(index);
    
    try {
      const response = await fetch(`${API_URL}/api/affirmations/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          affirmation_id: `aff_${Date.now()}_${index}`,
          content: affirmation
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to select affirmation');
      }

      setSelectedIndex(index);
      setTodaysAffirmation(affirmation);
      toast.success('Affirmation selected for today\'s visualisation!');
      
      // Notify parent component if callback provided
      if (onSelect) {
        onSelect(affirmation);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSelectingIndex(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-[#252528] border border-white/10 rounded-[28px] w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C6A75E]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-serif text-xl text-[#DCD3CA]">AI Affirmations</h2>
                <p className="text-[#7A7670] text-xs font-sans">Select one for today's visualisation</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-[#1C1C1E] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
              data-testid="close-affirmations-modal"
              aria-label="Close affirmations"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Today's Selection Banner */}
        {todaysAffirmation && (
          <div className="px-6 py-3 bg-[#8BAA9E]/10 border-b border-[#8BAA9E]/20 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#8BAA9E]" />
              <span className="text-[#8BAA9E] text-xs font-sans">Today's Visualization Affirmation:</span>
            </div>
            <p className="text-[#DCD3CA] text-sm font-serif mt-1 italic">"{todaysAffirmation}"</p>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {!hasGenerated ? (
            <div className="text-center py-6">
              <p className="text-[#B5B1AB] font-sans mb-6 text-sm">
                Generate personalized affirmations based on your identity statement and goals.
              </p>
              <div className="bg-[#1C1C1E] rounded-2xl p-4 mb-6 text-left">
                <p className="text-[#7A7670] text-xs uppercase tracking-wider mb-2 font-sans">Your Identity</p>
                <p className="font-serif text-[#DCD3CA] italic text-sm">"{goal.identity_statement}"</p>
              </div>
              <Button
                onClick={generateAffirmations}
                disabled={loading}
                className="bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full px-6 py-4 font-medium flex items-center gap-2 mx-auto"
                data-testid="generate-affirmations-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Affirmations
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[#7A7670] text-xs font-sans text-center mb-4">
                Tap an affirmation to select it for today's visualisation
              </p>
              
              {affirmations.map((affirmation, index) => (
                <button
                  key={affirmation.slice(0, 30) || `aff-${index}`}
                  onClick={() => handleSelectForVisualization(index)}
                  disabled={selectingIndex !== null}
                  className={`w-full text-left bg-[#1C1C1E] rounded-2xl p-5 border transition-all animate-slide-up ${
                    selectedIndex === index 
                      ? 'border-[#8BAA9E] bg-[#8BAA9E]/10' 
                      : todaysAffirmation === affirmation
                        ? 'border-[#C6A75E]/50'
                        : 'border-white/5 hover:border-[#8BAA9E]/50'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`affirmation-card-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                      selectedIndex === index || todaysAffirmation === affirmation
                        ? 'bg-[#8BAA9E]'
                        : 'bg-[#C6A75E]/20'
                    }`}>
                      {selectingIndex === index ? (
                        <RefreshCw className="w-3 h-3 text-white animate-spin" />
                      ) : selectedIndex === index || todaysAffirmation === affirmation ? (
                        <Check className="w-3 h-3 text-white" />
                      ) : (
                        <span className="text-[#C6A75E] text-xs font-sans">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-serif text-[#DCD3CA] leading-relaxed text-sm">{affirmation}</p>
                      {(selectedIndex === index || todaysAffirmation === affirmation) && (
                        <p className="text-[#8BAA9E] text-xs font-sans mt-2 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          Selected for visualisation
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              <Button
                onClick={generateAffirmations}
                disabled={loading}
                variant="outline"
                className="w-full border-[#666666] text-[#B5B1AB] hover:border-[#C6A75E] hover:text-[#C6A75E] rounded-full py-4 mt-4 flex items-center justify-center gap-2"
                data-testid="regenerate-affirmations-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Generate New
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Footer tip */}
        <div className="p-4 border-t border-white/5 bg-[#1C1C1E]/50 flex-shrink-0">
          <p className="text-[#7A7670] text-xs font-sans text-center">
            💡 Your selected affirmation will appear during visualisation
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffirmationsModal;
